import * as React from 'react';
import { Form, Text, Select } from 'react-form';
import { BaseFormProps } from '../../interfaces';
import { ScoreTypeDataService, SurveyVersionDataService, LanguageDataService, NotificationService, CharacteristicGroupDataService } from '../../services';
import { SurveyVersion, ScoreType, CharacteristicGroup, Language, CharacteristicText, CharacteristicScore } from '../../models';
import { PonderaEditModal } from '../../components/inclinationmodals';
import { PVARawScoreSelect } from '..';
import { UserStore } from '../../stores';
import { observer, inject } from 'mobx-react';
import { computed } from 'mobx';

interface CharacteristicFormProps extends BaseFormProps {
    notificationService: NotificationService;
    surveyVersionId: number;
    UserStore?: UserStore;
}

class CharacteristicFormState {
    scoreTypes: ScoreType[];
    characteristicGroups: CharacteristicGroup[];
    languages: Language[];
    surveyIsPublished: boolean;
}

@inject('UserStore') @observer
export class CharacteristicForm extends React.Component<CharacteristicFormProps, CharacteristicFormState> {
    private scoreTypeDataService = new ScoreTypeDataService(this.props.notificationService);
    private surveyVersionDataService = new SurveyVersionDataService(this.props.notificationService);
    private characteristicGroupDataService = new CharacteristicGroupDataService(this.props.notificationService);
    private languageDataService = new LanguageDataService(this.props.notificationService);

    @computed get isSuperAdmin() {
        return this.props.UserStore!.userRoleIn(['superAdmin']);
    }

    constructor(props: CharacteristicFormProps) {
        super(props);

        this.props.defaultValues.characteristicTexts = this.props.defaultValues.characteristicTexts || [];
        this.props.defaultValues.characteristicScores = this.props.defaultValues.characteristicScores || [];
        this.props.defaultValues.characteristicCharacteristicScores = this.props.defaultValues.characteristicCharacteristicScores || [];
        this.props.defaultValues.surveyVersionId = this.props.defaultValues.surveyVersionId || this.props.surveyVersionId;

        // Convert to percentages
        this.props.defaultValues.characteristicScores.map((cs: any) => {
            cs.lowScore *= 100.0;
            cs.highScore *= 100.0;
        });

        this.state = {
            scoreTypes: [],
            characteristicGroups: [],
            languages: [],
            surveyIsPublished: false
        };
    }

    public componentDidMount() {
        this.languageDataService.getAll().then((languages: Language[]) => {
            this.setState({
                languages: languages
            });
        });
        this.surveyVersionDataService.get(this.props.surveyVersionId).then((surveyVersion: SurveyVersion) => {
            this.setState({
                surveyIsPublished: surveyVersion.published !== null ? true : false
            });

            this.scoreTypeDataService.getAllBySurveyVersion(surveyVersion).then((scoreTypes: ScoreType[]) => {
                this.setState({
                    scoreTypes: scoreTypes
                });
            });
            this.characteristicGroupDataService.getAllBySurveyVersion(surveyVersion).then((characteristicGroups: CharacteristicGroup[]) => {
                this.setState({
                    characteristicGroups: characteristicGroups
                });
            });
        });
    }

    private removeCharScore(values: any, ind: number) {
        values.characteristicScores.splice(ind, 1);
        this.forceUpdate();
    }

    private addCharScore(values: any) {
        var cs = new CharacteristicScore();
        values.characteristicScores.push(cs);
        this.forceUpdate();
    }

    private submit(submittedValues: any) {
        // Convert percent back to decimal
        submittedValues.characteristicScores.map((cs: any) => {
            cs.lowScore /= 100.0;
            cs.highScore /= 100.0;
        });

        this.props.submit(submittedValues);
    }

    public render() {

        const characteristicGroupOptions: any = [];
        if (this.state.characteristicGroups) {
            this.state.characteristicGroups.map((x: CharacteristicGroup) => {
                characteristicGroupOptions.push({
                    label: x.name,
                    value: x.id
                });
            });
        }

        const characteristicScoreFocusTypeOptions: any = [
            {
                label: 'High',
                value: 'high'
            },
            {
                label: 'Low',
                value: 'low'
            },
            {
                label: 'Both',
                value: 'both'
            },
            {
                label: 'Never',
                value: 'never'
            }
        ];

        const characteristicFocusTypeOptions: any = [
            {
                label: 'Normal',
                value: 'normal'
            },
            {
                label: 'ResponseIndicator',
                value: 'responseIndicator'
            }   
        ];

        const { surveyIsPublished } = this.state;

        return (
            <Form
                defaultValues={this.props.defaultValues}
                onSubmit={submittedValues => this.submit(submittedValues)}
            >
                {formApi => (
                
                    <form onSubmit={formApi.submitForm} id="entityForm">
                        {surveyIsPublished && <div className="pva-alert">
                            <i>This survey version has already been published, so only content changes are allowed.</i>
                        </div>}
                        <fieldset>
                            <div className="form-group">
                                <label htmlFor="name">Characteristic Name *</label>
                                <Text className="form-control" field="name" id="name" disabled={!this.isSuperAdmin} />
                            </div>
                        </fieldset>
                        <fieldset>
                            <div className="form-group">
                                <label htmlFor="name">Characteristic Display Name (optional)</label>
                                <Text className="form-control" field="displayName" id="displayName" disabled={!this.isSuperAdmin} />
                            </div>
                        </fieldset>
                        {/* <fieldset disabled={surveyIsPublished}>
                        {this.isSuperAdmin &&
                            <div className="form-group">
                                <label htmlFor="name">Characteristic Group</label>
                                <div className="row">
                                    <div className="col-xs-10">
                                        <Select className="form-control" field="characteristicGroupID" id="characteristicGroupId" options={characteristicGroupOptions} />
                                    </div>
                                    <div className="col-xs-2">
                                        <a className="btn btn-warning btn-block" onClick={() => formApi.setValue('characteristicGroupID', undefined)}>Clear</a>
                                    </div>
                                </div>
                            </div>}
                        </fieldset> */}
                        <fieldset>
                            <label htmlFor="name">Characteristic Type</label>
                            <Select className="form-control" field="characteristicFocusType" id="characteristicFocusType" options={characteristicFocusTypeOptions} />
                        </fieldset>
                        {formApi.values.characteristicFocusType === 'responseIndicator' && <fieldset>
                            <label htmlFor="name">Sort</label>
                            <Text className="form-control" field="sort" id="sort" />
                        </fieldset>}
                        <hr />
                        {this.isSuperAdmin && <fieldset>
                            <div className="row">
                                <div className="col-xs-6">
                                    <div className="form-group">
                                        <label htmlFor="name">Mid Range Low</label>
                                        <Text className="form-control" field="midStart" id="midRangeLow" />
                                    </div>
                                </div>
                                <div className="col-xs-6">
                                    <div className="form-group">
                                        <label htmlFor="name">Mid Range High</label>
                                        <Text className="form-control" field="midEnd" id="midRangeHigh" />
                                    </div>
                                </div>
                            </div>
                            <hr />
                        </fieldset>}
                        <fieldset disabled={surveyIsPublished}>

                            {this.isSuperAdmin &&
                                <div>
                                    <h3>Scoring</h3>
                                    <i>Uses an individuals Raw Score to calculate the Characteristic User Score. The Raw Scores are determined by the users statement responses.</i>
                                    <div>
                                        {
                                            formApi.values.characteristicScores.map((data: any, ind: number) => {
                                                return <div key={ind}>
                                                    <div className="row">
                                                        <div className="col-xs-6">
                                                            <div className="form-group">
                                                                <label htmlFor="name">Raw Score</label>
                                                                <PVARawScoreSelect options={this.state.scoreTypes} onChange={(e: any) => this.setRawScore(formApi, ind, e)} selected={formApi.values.characteristicScores[ind].scoreTypeID} />
                                                            </div>
                                                        </div>
                                                        <div className="col-xs-2">
                                                            <div className="form-group">
                                                                <label htmlFor="name">High</label>
                                                                <Text className="form-control" field={['characteristicScores', ind, 'highScore']} id={'highScore' + ind} />
                                                            </div>
                                                        </div>
                                                        <div className="col-xs-2">
                                                            <div className="form-group">
                                                                <label htmlFor="name">Low</label>
                                                                <Text className="form-control" field={['characteristicScores', ind, 'lowScore']} id={'lowScore' + ind} />
                                                            </div>
                                                        </div>
                                                        <div className="col-xs-2">
                                                            <div className="form-group">
                                                                <label htmlFor="remove">&nbsp;</label>
                                                                <a className="btn btn-danger btn-block" onClick={(e) => this.removeCharScore(formApi.values, ind)}>Remove</a>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>;
                                            })
                                        }
                                        <br />
                                        <div className="form-group">
                                            <div className="row">
                                                <div className="col-xs-6">
                                                    <a className="btn btn-default" onClick={(e) => this.addCharScore(formApi.values)}>Add Score</a>
                                                </div>
                                                <div className="col-xs-4 text-center">
                                                    <b>Total: {this.totalScore(formApi.values)}</b>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>}
                        </fieldset>
                        <fieldset>
                            <hr />
                            <h3>Interaction Report</h3>
                            <i>These help determine when the Interaction Report will show this characteristic. ex: If the score is below the Low Score the characteristic will show.</i>
                            <div className="form-group">
                                <label htmlFor="name">Focus Type</label>
                                <Select className="form-control" field="characteristicScoreFocusType" id="characteristicScoreFocusType" options={characteristicScoreFocusTypeOptions} />
                            </div>
                            <div className="row">
                                <div className="col-xs-6">
                                    <div className="form-group">
                                        <label htmlFor="name">Low Score</label>
                                        <Text className="form-control" field="lowScore" id="lowScore" />
                                    </div>
                                </div>
                                <div className="col-xs-6">
                                    <div className="form-group">
                                        <label htmlFor="name">High Score</label>
                                        <Text className="form-control" field="highScore" id="highScore" />
                                    </div>
                                </div>
                            </div>
                            <hr />
                        </fieldset>
                        <fieldset>

                            {this.isSuperAdmin && <div>
                                    <div>
                                        <i>The interaction report content requires it be setup with a Bullet List. Each bullet list item will be styled as a bubble with either green or red background, depending on if it's on the hurt or helpful side.</i>
                                    </div>
                                    <div>
                                        <i><b>To force this characteristic to show regardless of score, use each PVA Style's edit page, and add this characteristic to the list of Interaction Report Force.</b></i>
                                    </div>

                                    <div className="row">
                                        <div className="col-sm-6">
                                            {this.ShowTextBlocks('High Help Text', 'helP_TEXT', formApi.values, 'When the Score Focus is set to High and the score is over the High Score')}
                                        </div>
                                        <div className="col-sm-6">
                                            {this.ShowTextBlocks('High Hurt Text', 'hurT_TEXT', formApi.values, 'When the Score Focus is set to High and the score is over the High Score')}
                                        </div>
                                    </div>
                                    
                                    <div className="row">
                                        <div className="col-sm-6">
                                            {this.ShowTextBlocks('Low Help Text', 'loW_HELP', formApi.values, 'When the Score Focus is set to Low and the score is under the Low Score')}
                                        </div>
                                        <div className="col-sm-6">
                                            {this.ShowTextBlocks('Low Hurt Text', 'loW_HURT', formApi.values, 'When the Score Focus is set to Low and the score is under the Low Score')}
                                        </div>
                                    </div>
                                </div>}
                        </fieldset>

                        <h3>Content</h3>
                        {this.ShowTextBlocks('Tooltips', 'tooltip', formApi.values)}
                        {this.ShowTextBlocks('Description', 'description', formApi.values)}
                        {this.ShowTextBlocks('About Text', 'higH_TEXT', formApi.values)}

                        <button type="submit" className="btn btn-primary">Save</button>
                    </form>
                )}
            </Form>
        );
    }

    totalScore(formApiValues: any) {
        var result = 0;
        formApiValues.characteristicScores.map((x: any) => {
            if (x.highScore) {
                result += parseFloat(x.highScore);
            }
            if (x.lowScore) {
                result += parseFloat(x.lowScore);
            }
        });
        return result;
    }

    private ShowTextBlocks(title: string, textType: string, formApiValues: any, desc?: string) {

        const languageOpts: any = [];
        if (this.state.languages) {
            this.state.languages.map((x: Language) => {
                languageOpts.push({
                    label: x.name,
                    value: x.id
                });
            });
        }

        return (
            <div>
                <h5><b>{title}</b></h5>
                {desc && <span><i>{desc}</i></span>}
                <table className="table">
                    <thead>
                        <tr>
                            <td>
                                Language
                        </td>
                            <td>
                                Text
                        </td>
                        </tr>
                    </thead>
                    <tbody>
                        {formApiValues.characteristicTexts.map((ct: CharacteristicText, ind: number) => {
                            if (ct.textType !== textType) {
                                return;
                            }

                            const props = {
                                show: ct.showModal === true,
                                onHide: (data: string) => {
                                    ct.showModal = false;
                                    ct.value = data;
                                    this.forceUpdate();
                                },
                                title: 'Content',
                                content: ct.value || ''
                            };

                            return <tr key={`char-text-${ind}`}>
                                <td>
                                    <Select className="form-control" field={['characteristicTexts', ind, 'languageId']} options={languageOpts} />
                                </td>
                                <td>
                                    <a className="btn btn-default" onClick={(e) => { ct.showModal = true; this.forceUpdate(); }}>View/Edit</a>
                                    <PonderaEditModal {...props} />
                                </td>
                            </tr>;
                        })}
                    </tbody>
                </table>

                <div className="form-group">
                    <div className="row">
                        <div className="col-xs-4">
                            <a className="btn btn-default form-control" onClick={(e) => this.addContent(formApiValues, textType)}>Add Content</a>
                        </div>
                    </div>
                </div>
            </div>);
    }

    private addContent(formValues: any, textType: string) {
        var ct = new CharacteristicText();
        ct.textType = textType;
        formValues.characteristicTexts.push(ct);
        this.forceUpdate();
    }

    private setRawScore(formApi: any, ind: number, e: any) {
        var id = parseInt(e.target.value, 10);
        var characteristicScores = formApi.values.characteristicScores;
        characteristicScores[ind].scoreTypeID = id;
        formApi.setValue('characteristicScores', characteristicScores);
        this.forceUpdate();
    }
}



// WEBPACK FOOTER //
// ./src/components/characteristic/CharacteristicForm.tsx